import React, { Component } from 'react';
import Button from 'devextreme-react/button';
import DataGrid, {
    Column,
    ColumnFixing,
    Editing,
    FilterRow,
    HeaderFilter,
    Lookup,
    Paging,
    RemoteOperations,
    RequiredRule,
    Scrolling,
    Sorting
} from 'devextreme-react/data-grid';
import * as AspNetData from 'devextreme-aspnet-data-nojquery';
import config from '../../common/config/config';
import { CustomRule } from 'devextreme-react/validator';
import { startSync } from '../../common/syncUtils';

const dataSource = AspNetData.createStore({
    key: 'id',
    loadUrl: `${config.apiUrl}/lead-metrics`,
    insertUrl: `${config.apiUrl}/lead-metrics`,
    updateUrl: `${config.apiUrl}/lead-metrics`,
    deleteUrl: `${config.apiUrl}/lead-metrics`
});

const statusDataSource = [
    { id: 'preliminary', name: 'preliminary' },
    { id: 'final', name: 'final' }
];

const endpoint = 'job/start/transactional/lead-metrics-import';

class LeadMetricsComponent extends Component {

    constructor(props) {
        super(props);

        this.state = {
            currentPage: 0,
            pageLimit: 10,
            totalRecords: 0,
            pageCount: 0,
            pageNeighbours: 2,
            showFilterRow: true,
            showHeaderFilter: true,
            currentFilter: 'auto',
            selectTextOnEditStart: true,
            startEditAction: 'click',
            errors: [],
            isAuthenticated: false,
            user: undefined,
            mode: 'nextColumn',
            readOnly: true,
            height: window.innerHeight - config.pxToSubtract
        };
    }

    handleSync = () => {
        try {
            startSync(endpoint);
        } catch (error) {
            console.error(error);
        }
    };

    render() {
        return (
            <div>
                <h1>Lead Metrics</h1>

                <div>
                    <ul style={{ listStyleType: 'none' }}>
                        {this.state.errors.map((value, index) => {
                            return <li style={{ color: 'red' }} key={index}>{value}</li>;
                        })}
                    </ul>
                </div>

                <div className="datagrid">
                    <Button text="Sync" onClick={this.handleSync} />

                    <DataGrid
                        id="gridContainer"
                        dataSource={dataSource}
                        showBorders={true}
                        keyExpr="id"
                        rowAlternationEnabled={true}
                        showRowLines={true}
                        allowColumnResizing={true}
                        columnResizingMode={'nextColumn'}
                        allowColumnReordering={true}
                        columnAutoWidth={true}
                        height={this.state.height}
                    >
                        <ColumnFixing enabled="true" />
                        <Sorting mode="multiple" />
                        <RemoteOperations paging={true} filtering={true} sorting={true} grouping={true} />
                        <Paging defaultPageSize={config.defaultPageSize} />
                        <Scrolling mode="virtual" rowRenderingMode="virtual" />

                        <Editing
                            mode="batch"
                            allowUpdating={!this.state.readOnly}
                            allowAdding={!this.state.readOnly}
                            allowDeleting={!this.state.readOnly}
                        />

                        <FilterRow
                            visible={this.state.showFilterRow}
                            applyFilter={this.state.currentFilter}
                            showAllText=""
                        />

                        <HeaderFilter visible={this.state.showHeaderFilter} />

                        <Column dataField="dataAsOf" dataType="date">
                            <RequiredRule message="Data as of is required" />
                        </Column>

                        <Column dataField="kvpsId">
                            <RequiredRule message="KVPS ID is required" />
                            <CustomRule
                                reevaluate={true}
                                validationCallback={this.kvpsIdValidator}
                                message="KVPS ID must match format DEU28100A"
                            />
                        </Column>

                        <Column
                            dataField="month"
                            dataType="date"
                            format="yyyy-MM"
                            setCellValue={this.setMonthValue}
                            editorOptions={{
                                displayFormat: "yyyy-MM",
                                dateSerializationFormat: "yyyy-MM",
                                calendarOptions: {
                                    zoomLevel: "year",
                                    maxZoomLevel: "year"
                                }
                            }}
                        >
                            <RequiredRule message="Month is required" />
                        </Column>

                        <Column
                            dataField="leadCount"
                            dataType="number"
                            setCellValue={this.setLeadCountValue}
                        >
                            <RequiredRule message="Lead Count is required" />
                            <CustomRule
                                reevaluate={true}
                                validationCallback={this.nonNegativeIntegerOrEmptyValidator}
                                message="Lead Count must be a non-negative integer"
                            />
                            <CustomRule
                                reevaluate={true}
                                validationCallback={this.leadCountValidator}
                                message="Lead Count must be greater than or equal to Lead Count With Revenue"
                            />
                        </Column>

                        <Column
                            dataField="leadCountWithRevenue"
                            dataType="number"
                            setCellValue={this.setLeadCountWithRevenueValue}
                        >
                            <RequiredRule message="Lead Count With Revenue is required" />
                            <CustomRule
                                reevaluate={true}
                                validationCallback={this.nonNegativeIntegerOrEmptyValidator}
                                message="Lead Count With Revenue must be a non-negative integer"
                            />
                            <CustomRule
                                reevaluate={true}
                                validationCallback={this.leadCountWithRevenueValidator}
                                message="Lead Count With Revenue must not exceed Lead Count"
                            />
                        </Column>

                        <Column dataField="conversionRate" dataType="number" allowEditing={false} />

                        <Column dataField="status">
                            <RequiredRule message="Status is required" />
                            <Lookup
                                dataSource={statusDataSource}
                                valueExpr="id"
                                displayExpr="name"
                            />
                        </Column>
                    </DataGrid>
                </div>
            </div>
        );
    }

    kvpsIdValidator = (data) => {
        if (data.value === undefined || data.value === null || data.value === '') {
            return false;
        }

        return /^[A-Z]{3}[0-9]{5}[A-Z]$/.test(data.value);
    };

    nonNegativeIntegerOrEmptyValidator = (data) => {
        if (data.value === undefined || data.value === null || data.value === '') {
            return true;
        }

        return Number.isInteger(data.value) && data.value >= 0;
    };

    setMonthValue = (newData, value) => {
        if (value === undefined || value === null || value === '') {
            newData.month = value;
            return;
        }

        if (typeof value === 'string') {
            const yearMonth = value.match(/^(\d{4})-(\d{2})/);
            if (yearMonth) {
                newData.month = `${yearMonth[1]}-${yearMonth[2]}`;
                return;
            }
        }

        const selectedMonth = value instanceof Date ? value : new Date(value);
        const year = selectedMonth.getFullYear();
        const month = String(selectedMonth.getMonth() + 1).padStart(2, '0');
        newData.month = `${year}-${month}`;
    };

    leadCountValidator = (data) => {
        if (data.value === undefined || data.value === null || data.value === '' ||
            data.data.leadCountWithRevenue === undefined || data.data.leadCountWithRevenue === null ||
            data.data.leadCountWithRevenue === '') {
            return true;
        }

        return data.value >= data.data.leadCountWithRevenue;
    };

    leadCountWithRevenueValidator = (data) => {
        if (data.value === undefined || data.value === null || data.value === '' ||
            data.data.leadCount === undefined || data.data.leadCount === null ||
            data.data.leadCount === '') {
            return true;
        }

        return data.value <= data.data.leadCount;
    };

    setLeadCountValue = (newData, value, currentRowData) => {
        newData.leadCount = value;
        const leadCountWithRevenue = Object.prototype.hasOwnProperty.call(newData, 'leadCountWithRevenue')
            ? newData.leadCountWithRevenue
            : currentRowData.leadCountWithRevenue;
        newData.conversionRate = this.calculateConversionRate(value, leadCountWithRevenue);
    };

    setLeadCountWithRevenueValue = (newData, value, currentRowData) => {
        newData.leadCountWithRevenue = value;
        const leadCount = Object.prototype.hasOwnProperty.call(newData, 'leadCount')
            ? newData.leadCount
            : currentRowData.leadCount;
        newData.conversionRate = this.calculateConversionRate(leadCount, value);
    };

    calculateConversionRate = (leadCount, leadCountWithRevenue) => {
        if (leadCount === undefined || leadCount === null || leadCount === '' ||
            leadCountWithRevenue === undefined || leadCountWithRevenue === null ||
            leadCountWithRevenue === '') {
            return null;
        }

        return leadCount === 0 ? 0 : leadCountWithRevenue / leadCount;
    };

    componentDidMount() {
        fetch(`${config.authUrl}`)
            .then(response => {
                response.json()
                    .then(body => {
                        if (body['authenticated'] === false) {
                            this.setState({ isAuthenticated: false });
                            window.location.reload();
                        } else {
                            this.setState({
                                isAuthenticated: true,
                                readOnly: body.readOnly
                            });
                        }
                    });
            });

        window.addEventListener('resize', this.updateDimensions.bind(this));
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateDimensions.bind(this));
    }

    updateDimensions() {
        this.setState({ height: parseInt(window.innerHeight) - config.pxToSubtract });
    }
}

export default LeadMetricsComponent;
